import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useSession, useAsync } from '../lib/session';
import { money, short, date } from '../lib/fmt';
import type { InvoiceRow } from '../lib/types';
import { Card, Field, Input, Select, Note, PageHead, Pill, Table, Th, Td,
         TableWrap, Empty } from '../components/ui';
import { Loading, TableSkeleton } from '../components/skeleton';

export default function Invoices() {
  const { fy } = useSession();
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const feed = useAsync<InvoiceRow[]>(
    () => api.get('/api/invoices', { fy, q, status }), [fy, q, status]);

  const rows = feed.data ?? [];
  const net = rows.reduce((a, r) =>
    a + Math.round(r.subtotal * r.fx_rate) * (r.doc_type === 'credit_note' ? -1 : 1), 0);
  const outstanding = rows.reduce((a, r) =>
    a + Math.round((r.settlement?.outstanding ?? 0) * r.fx_rate), 0);

  return (
    <div>
      <PageHead eyebrow={`FY ${fy ?? ''} · invoice register`} title="Invoices"
        dek="Every invoice and credit note, newest first. Click a row to open it." />

      <div className="flex flex-wrap items-end gap-2.5 mb-3">
        <Field label="Search"><Input value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Number or note…" className="min-w-[220px]" /></Field>
        <Field label="Status">
          <Select value={status} onChange={(e) => setStatus(e.target.value)} className="min-w-[150px]">
            <option value="">Any status</option>
            {['draft','sent','part_paid','paid','cancelled'].map((s) =>
              <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
          </Select>
        </Field>
      </div>

      <p className="text-[13.5px] text-ink-2 mb-3">
        {rows.length} document{rows.length === 1 ? '' : 's'} · net <b>{short(net)}</b>
        {outstanding > 0 && <> · outstanding <b>{short(outstanding)}</b></>}
      </p>

      {feed.error && <Note title="That did not load" tone="critical">{feed.error}</Note>}

      <Card flush>
        {feed.loading ? <Loading label="Loading invoices"><TableSkeleton rows={8} cols={8} /></Loading>
         : !rows.length ? <Empty>No documents match those filters.</Empty>
         : (
          <TableWrap scroll>
            <Table cards>
              <thead><tr>
                <Th>Number</Th><Th>Date</Th><Th>Client</Th><Th>Description</Th>
                <Th>Status</Th><Th n>Taxable</Th><Th n>Total</Th><Th n>Due</Th>
              </tr></thead>
              <tbody>
                {rows.map((r) => {
                  const sign = r.doc_type === 'credit_note' ? -1 : 1;
                  const out = r.settlement?.outstanding ?? 0;
                  const overdue = out > 0 && r.due_date && r.due_date < new Date().toISOString().slice(0, 10)
                                  && !['paid','draft','cancelled'].includes(r.status);
                  return (
                    <tr key={r._id} tabIndex={0} className="cursor-pointer hover:bg-card-2"
                        onClick={() => nav(`/invoices/${r._id}`)}
                        onKeyDown={(e) => { if (e.key === 'Enter') nav(`/invoices/${r._id}`); }}>
                      <Td label="" className="font-mono text-[12.5px] font-semibold">{r.number}</Td>
                      <Td label="Date" className="font-mono text-[11.5px]">{date(r.issue_date)}</Td>
                      <Td label="Client">{typeof r.client === 'object' ? r.client.name : '—'}</Td>
                      <Td label="Description" className="text-ink-2">{r.project?.name ?? '—'}</Td>
                      <Td label="Status"><Pill kind={r.doc_type === 'credit_note' ? 'credit_note' : overdue ? 'overdue' : r.status}>
                        {r.doc_type === 'credit_note' ? 'Credit note' : overdue ? 'Overdue' : r.status.replace('_', ' ')}
                      </Pill></Td>
                      <Td n label="Taxable">{money(r.subtotal * sign, r.currency)}</Td>
                      <Td n label="Total">{money(r.total * sign, r.currency)}</Td>
                      <Td n label="Due" className={out > 0 ? 'text-critical' : 'text-muted'}>
                        {r.doc_type === 'credit_note' ? '—' : out > 0 ? money(out, r.currency) : 'settled'}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Card>
    </div>
  );
}
